if(!customElements.get('bundle-products')){
    class BundleProducts extends HTMLElement {
        constructor(){
            super();
        }

        connectedCallback(){
            // variantId -> { qty, price (cents), title, image, card }
            this.items = new Map();
            this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
            const currency = this.dataset.currency || window.Shopify?.currency?.active;
            const foreignCurrency = currency && this.dataset.shopCurrency && currency !== this.dataset.shopCurrency;
            const rate = parseFloat(window.Shopify?.currency?.rate);
            const hasRate = Number.isFinite(rate) && rate > 0;
            const exchangeRate = foreignCurrency && hasRate ? rate : 1;
            // in a foreign presentment currency every amount this component
            // prints comes from presentment cents - format them all in that
            // currency (shop.money_format would show the shop's symbol)
            this.presentmentFormatter = foreignCurrency ? new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
                style: 'currency',
                currency: currency,
            }) : null;
            this.savingPercent = parseFloat(this.dataset.savingPercentage) || 0;
            this.discountType = this.dataset.discountType || 'percentage';
            this.minRequirement = this.dataset.minRequirement || 'none';
            this.minQuantity = parseInt(this.dataset.minQuantity, 10) || 0;
            // Settings are in shop currency; Liquid variant prices are already
            // in the shopper's currency. Convert the configured amounts.
            this.savingFixed = Math.round((parseInt(this.dataset.savingFixed, 10) || 0) * exchangeRate);
            this.minPrice = Math.round((parseFloat(this.dataset.minPrice) || 0) * exchangeRate);
            if(foreignCurrency && !hasRate){
                // without a usable rate the threshold can't be converted -
                // don't enforce a wrong one (better no gate than a bogus gate)
                if(this.minRequirement === 'price') this.minRequirement = 'none';
            }
            this.requirementMet = true;
            this.busy = false;

            this.emptyEl = this.querySelector('.bundle-products__empty');
            this.listEl = this.querySelector('.bundle-products__items');
            this.totalsEl = this.querySelector('.bundle-products__totals');
            this.subtotalEl = this.querySelector('.bundle-products__subtotal-value');
            this.savingsSummaryEl = this.querySelector('.bundle-products__savings-summary');
            this.savingsEl = this.querySelector('.bundle-products__savings-value');
            this.totalEl = this.querySelector('.bundle-products__total-value');
            this.addToCartBtn = this.querySelector('.bundle-products__add-to-cart');
            this.errorEl = this.querySelector('.bundle-products__error');
            this.requirementEl = this.querySelector('.bundle-products__requirement');
            this.requirementMessageEl = this.querySelector('.bundle-products__requirement-message');
            this.requirementProgressEl = this.querySelector('.bundle-products__requirement-progress');

            // mobile floating panel (pinned to the bottom of the screen)
            this.panelEl = this.querySelector('.bundle-products__bundle');
            this.mobileBar = this.querySelector('.bundle-products__mobile-bar');
            this.mobileCountEl = this.querySelector('.bundle-products__mobile-count');
            this.mobileSubtotalEl = this.querySelector('.bundle-products__mobile-subtotal');
            this.lastTotalQty = 0;

            this.addEventListener('click', this.onClick.bind(this));
            this.addEventListener('change', this.onChange.bind(this));
            if(this.addToCartBtn){
                this.addToCartBtn.addEventListener('click', this.addBundleToCart.bind(this));
            }
            if(this.mobileBar && this.panelEl){
                this.mobileBar.addEventListener('click', this.toggleMobilePanel.bind(this));
            }
            this.observePanel();
        }

        toggleMobilePanel(){
            const inner = this.panelEl.querySelector('.bundle-products__bundle-inner');
            // current visual height, mid-animation included, so a tap during
            // the slide reverses smoothly from wherever the sheet is now
            const from = inner ? inner.offsetHeight : 0;

            const expanded = this.panelEl.toggleAttribute('data-expanded');
            this.mobileBar.setAttribute('aria-expanded', expanded ? 'true' : 'false');

            const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if(!inner || reduceMotion) return;

            // unpin any running slide so the new target height is measurable
            if(this.slideCleanup) this.slideCleanup();

            // the collapsed rows toggle via display, which can't transition -
            // slide the sheet between its measured heights instead (FLIP)
            const to = inner.offsetHeight;
            if(from === to) return;

            inner.classList.add('is-sliding');
            inner.style.height = `${from}px`;
            void inner.offsetHeight;
            inner.style.height = `${to}px`;

            const done = () => {
                clearTimeout(this.slideTimer);
                inner.classList.remove('is-sliding');
                inner.style.height = '';
                inner.removeEventListener('transitionend', onEnd);
                this.slideCleanup = null;
                this.updatePanelOffset();
            };
            const onEnd = e => {
                if(e.target === inner && e.propertyName === 'height') done();
            };
            inner.addEventListener('transitionend', onEnd);
            this.slideCleanup = done;
            // safety net in case transitionend never fires (e.g. tab hidden);
            // deadline derived from the CSS so a retuned duration stays in sync
            const duration = (parseFloat(getComputedStyle(inner).transitionDuration) || 0.45) * 1000;
            this.slideTimer = setTimeout(done, duration + 150);
        }

        observePanel(){
            if(!this.panelEl) return;

            this.mobileQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 992px)') : null;
            if(this.mobileQuery && this.mobileQuery.addEventListener){
                this.mobileQuery.addEventListener('change', () => this.publishBarHeight());
            }

            // slide the floating bar out of the way while the section is
            // off-screen; an open sheet is collapsed first so it can't keep
            // covering unrelated content further down the page
            if(typeof IntersectionObserver !== 'undefined'){
                this.panelObserver = new IntersectionObserver(entries => {
                    const onScreen = entries[entries.length - 1].isIntersecting;
                    if(!onScreen && this.panelEl.hasAttribute('data-expanded')){
                        this.panelEl.removeAttribute('data-expanded');
                        if(this.mobileBar) this.mobileBar.setAttribute('aria-expanded', 'false');
                    }
                    this.panelEl.setAttribute('data-offscreen', onScreen ? 'false' : 'true');
                    this.publishBarHeight();
                }, { rootMargin: '100px 0px' });
                this.panelObserver.observe(this);
            }

            // keep the end of the product grid reachable above the floating
            // panel, whose height changes with its content
            if(typeof ResizeObserver !== 'undefined'){
                this.panelResizeObserver = new ResizeObserver(() => {
                    this.updatePanelOffset();
                    this.publishBarHeight();
                });
                this.panelResizeObserver.observe(this.panelEl);
            }
        }

        updatePanelOffset(){
            // Expanded and animating panels overlay the grid. Only reserve
            // the settled collapsed height, including after a closing slide.
            if(!this.panelEl || this.panelEl.hasAttribute('data-expanded') || this.slideCleanup) return;
            this.style.setProperty('--bundle-panel-offset', `${this.panelEl.offsetHeight}px`);
        }

        // floating controls (move-to-top) read this root variable to stay
        // clear of the bar - same contract as --add-to-cart-bar-height
        publishBarHeight(){
            if(!this.panelEl) return;
            const mobile = !this.mobileQuery || this.mobileQuery.matches;
            const hidden = this.panelEl.getAttribute('data-offscreen') === 'true' && !this.panelEl.hasAttribute('data-expanded');
            const height = mobile && !hidden ? this.panelEl.offsetHeight : 0;
            document.documentElement.style.setProperty('--bundle-bar-height', `${height}px`);
        }

        disconnectedCallback(){
            if(this.panelObserver) this.panelObserver.disconnect();
            if(this.panelResizeObserver) this.panelResizeObserver.disconnect();
            clearTimeout(this.slideTimer);
            document.documentElement.style.setProperty('--bundle-bar-height', '0px');
        }

        onChange(event){
            const select = event.target.closest('.bundle-products__variant-select');
            if(select) this.onVariantChange(select);
        }

        // the card now targets the newly selected variant: retarget its data
        // attributes, price display, and stepper (quantities stay per variant)
        onVariantChange(select){
            const card = select.closest('.bundle-products__card');
            const option = select.selectedOptions[0];
            if(!card || !option) return;

            card.dataset.variantId = option.value;
            card.dataset.price = option.dataset.price || '0';
            card.dataset.image = option.dataset.image || '';

            const variantTitle = option.dataset.variantTitle || '';
            const baseTitle = card.dataset.baseTitle || card.dataset.title || '';
            card.dataset.title = variantTitle && variantTitle !== 'Default Title'
                ? `${baseTitle} - ${variantTitle}`
                : baseTitle;

            const price = parseInt(option.dataset.price, 10) || 0;
            const compare = parseInt(option.dataset.comparePrice, 10) || 0;
            const priceEl = card.querySelector('.bundle-products__card-current-price');
            const oldEl = card.querySelector('.bundle-products__card-old-price');
            if(priceEl) priceEl.textContent = this.formatMoney(price);
            if(oldEl){
                oldEl.textContent = this.formatMoney(compare);
                oldEl.setAttribute('data-hidden', compare > price ? 'false' : 'true');
            }

            this.syncCardControls(card);
        }

        onClick(event){
            const addBtn = event.target.closest('[data-bundle-add]');
            const plusBtn = event.target.closest('[data-bundle-plus]');
            const minusBtn = event.target.closest('[data-bundle-minus]');
            const removeBtn = event.target.closest('[data-bundle-remove]');

            if(addBtn || plusBtn){
                this.changeQty(event.target.closest('.bundle-products__card'), 1);
            }else if(minusBtn){
                this.changeQty(event.target.closest('.bundle-products__card'), -1);
            }else if(removeBtn){
                this.setQty(removeBtn.dataset.variantId, 0);
            }
        }

        changeQty(card, delta){
            if(!card || !card.dataset.variantId) return;
            const id = card.dataset.variantId;
            const current = this.items.has(id) ? this.items.get(id).qty : 0;

            if(!this.items.has(id)){
                this.items.set(id, {
                    qty: 0,
                    price: parseInt(card.dataset.price, 10) || 0,
                    title: card.dataset.title || '',
                    image: card.dataset.image || '',
                    card: card,
                });
            }
            this.setQty(id, current + delta);
        }

        setQty(id, qty){
            const item = this.items.get(id);
            if(!item) return;

            item.qty = Math.max(0, qty);
            if(item.qty === 0){
                this.items.delete(id);
            }
            this.syncCard(id, item);
            this.render();
        }

        syncCard(id, item){
            const card = item.card;
            if(!card) return;
            // a multi-variant card may meanwhile point at another variant -
            // its controls then reflect that variant, not this one
            if(card.dataset.variantId !== id) return;
            this.syncCardControls(card);
        }

        // reflect the bundle quantity of the card's currently selected variant
        syncCardControls(card){
            const id = card.dataset.variantId;
            const qty = this.items.has(id) ? this.items.get(id).qty : 0;
            const addBtn = card.querySelector('[data-bundle-add]');
            const stepper = card.querySelector('.bundle-products__stepper');
            const qtyEl = card.querySelector('.bundle-products__card-qty');

            if(addBtn) addBtn.setAttribute('data-hidden', qty > 0 ? 'true' : 'false');
            if(stepper) stepper.setAttribute('data-hidden', qty > 0 ? 'false' : 'true');
            if(qtyEl) qtyEl.textContent = qty;
        }

        render(){
            const hasItems = this.items.size > 0;

            if(this.emptyEl) this.emptyEl.setAttribute('data-hidden', hasItems ? 'true' : 'false');
            if(this.listEl) this.listEl.setAttribute('data-hidden', hasItems ? 'false' : 'true');
            if(this.totalsEl) this.totalsEl.setAttribute('data-hidden', hasItems ? 'false' : 'true');
            this.showError(false);

            if(this.listEl){
                this.listEl.innerHTML = '';
                this.items.forEach((item, id) => {
                    this.listEl.appendChild(this.buildRow(id, item));
                });
            }

            let subtotal = 0;
            let totalQty = 0;
            this.items.forEach(item => {
                subtotal += item.price * item.qty;
                totalQty += item.qty;
            });

            if(this.mobileCountEl) this.mobileCountEl.textContent = `(${totalQty})`;
            if(this.mobileSubtotalEl) this.mobileSubtotalEl.textContent = hasItems ? this.formatMoney(subtotal) : '';
            // replay the count bump on each addition as feedback near the thumb
            if(this.mobileBar && totalQty > this.lastTotalQty){
                this.mobileBar.classList.remove('bundle-bar-bump');
                void this.mobileBar.offsetWidth;
                this.mobileBar.classList.add('bundle-bar-bump');
            }
            this.lastTotalQty = totalQty;

            this.requirementMet = this.updateRequirement(hasItems, subtotal, totalQty);
            this.syncAddToCart();
            // don't promise the discount while the minimum requirement isn't met
            if(this.savingsSummaryEl){
                this.savingsSummaryEl.setAttribute('data-hidden', this.requirementMet ? 'false' : 'true');
            }
            // a fixed discount can never exceed what's in the bundle
            const savings = this.discountType === 'fixed_amount'
                ? Math.min(this.savingFixed, subtotal)
                : Math.round(subtotal * this.savingPercent / 100);

            if(this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(subtotal);
            if(this.savingsEl) this.savingsEl.textContent = `-${this.formatMoney(savings)}`;
            if(this.totalEl) this.totalEl.textContent = this.formatMoney(subtotal - savings);
        }

        // returns whether the minimum requirement is satisfied; shows the
        // remaining-progress hint while items are in the bundle but short
        updateRequirement(hasItems, subtotal, totalQty){
            if(this.minRequirement === 'none') return true;

            let met = true;
            let progress = 1;
            let message = '';

            if(this.minRequirement === 'quantity' && this.minQuantity > 0){
                met = totalQty >= this.minQuantity;
                progress = totalQty / this.minQuantity;
                const remaining = this.minQuantity - totalQty;
                const template = remaining === 1
                    ? (this.dataset.minQtyMessageOne || '')
                    : (this.dataset.minQtyMessageOther || '');
                message = this.escapeHtml(template).replace('[count]', `<strong>${remaining}</strong>`);
            }else if(this.minRequirement === 'price' && this.minPrice > 0){
                met = subtotal >= this.minPrice;
                progress = subtotal / this.minPrice;
                const remainingAmount = this.escapeHtml(this.formatMoney(this.minPrice - subtotal));
                message = this.escapeHtml(this.dataset.minPriceMessage || '').replace('[amount]', `<strong>${remainingAmount}</strong>`);
            }

            if(this.requirementEl){
                this.requirementEl.setAttribute('data-hidden', hasItems && !met ? 'false' : 'true');
            }
            // built from escaped parts above - only the <strong> tags are markup
            if(this.requirementMessageEl) this.requirementMessageEl.innerHTML = message;
            if(this.requirementProgressEl){
                this.requirementProgressEl.style.width = `${Math.min(100, Math.round(progress * 100))}%`;
            }
            return met;
        }

        buildRow(id, item){
            const li = document.createElement('li');
            li.className = 'bundle-products__item d-flex align-items-center py-2';

            const img = document.createElement('img');
            img.className = 'bundle-products__item-image';
            img.alt = '';
            img.loading = 'lazy';
            if(item.image) img.src = item.image;

            const info = document.createElement('div');
            info.className = 'bundle-products__item-info flex-1 px-3';

            const title = document.createElement('small');
            title.className = 'd-block';
            title.textContent = item.title;

            const meta = document.createElement('small');
            meta.className = 'bundle-products__item-meta d-block';
            meta.textContent = `${item.qty} × ${this.formatMoney(item.price)}`;

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'bundle-products__item-remove cursor-pointer p-0';
            remove.setAttribute('data-bundle-remove', '');
            remove.setAttribute('data-variant-id', id);
            remove.setAttribute('aria-label', `${this.dataset.removeLabel || 'Remove'} ${item.title}`);
            remove.innerHTML = '&times;';

            info.appendChild(title);
            info.appendChild(meta);
            li.appendChild(img);
            li.appendChild(info);
            li.appendChild(remove);
            return li;
        }

        addBundleToCart(){
            if(this.items.size === 0) return;

            const items = [];
            this.items.forEach((item, id) => {
                items.push({ id: parseInt(id, 10), quantity: item.qty });
            });

            this.setBusy(true);
            this.showError(false);
            const config = fetchConfig();
            config.body = JSON.stringify({ items: items });

            fetch(routes.cart_add_url, config)
            .then(res => res.json())
            .then(res => {
                if(res.status && res.status !== 200){
                    throw new Error(res.description || 'add to cart failed');
                }

                // the bundle keeps its selection so the shopper can
                // adjust and add again
                const drawer = document.querySelector('cart-drawer');
                if(drawer && typeof drawer.refreshFromServer === 'function'){
                    drawer.refreshFromServer(true);
                }else{
                    window.location.href = routes.cart_url;
                }
            })
            .catch(e => {
                console.error(e);
                this.showError(true);
            })
            .finally(() => {
                this.setBusy(false);
            });
        }

        setBusy(busy){
            this.busy = busy;
            this.syncAddToCart();
        }

        // single source of truth for the add-to-cart button state, so a
        // render during an in-flight request can't drop the busy-disable
        syncAddToCart(){
            if(!this.addToCartBtn) return;
            const hasItems = this.items.size > 0;
            this.addToCartBtn.toggleAttribute('disabled', this.busy || !hasItems || !this.requirementMet);
            this.addToCartBtn.setAttribute('data-hidden', hasItems ? 'false' : 'true');
        }

        escapeHtml(text){
            return String(text)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
        }

        showError(show){
            if(this.errorEl) this.errorEl.setAttribute('data-hidden', show ? 'false' : 'true');
        }

        // mirrors Shopify's money filter for the shop's money format; in a
        // foreign presentment currency, formats in that currency instead so
        // every amount in the panel carries the shopper's symbol
        formatMoney(cents){
            if(this.presentmentFormatter) return this.presentmentFormatter.format(cents / 100);
            const format = this.moneyFormat;
            const amount = cents / 100;

            function withDelimiters(number, precision, thousands, decimal){
                thousands = thousands || ',';
                decimal = decimal || '.';
                const parts = number.toFixed(precision).split('.');
                parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
                return parts.length > 1 ? parts.join(decimal) : parts[0];
            }

            let value = '';
            if(format.includes('{{amount_no_decimals_with_comma_separator}}')){
                value = format.replace('{{amount_no_decimals_with_comma_separator}}', withDelimiters(amount, 0, '.', ','));
            }else if(format.includes('{{amount_with_comma_separator}}')){
                value = format.replace('{{amount_with_comma_separator}}', withDelimiters(amount, 2, '.', ','));
            }else if(format.includes('{{amount_no_decimals}}')){
                value = format.replace('{{amount_no_decimals}}', withDelimiters(amount, 0));
            }else if(format.includes('{{amount}}')){
                value = format.replace('{{amount}}', withDelimiters(amount, 2));
            }else{
                value = withDelimiters(amount, 2);
            }
            return value.trim();
        }
    }

    customElements.define('bundle-products', BundleProducts);
}
